/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import React, { useState, useEffect, useRef } from "react";
import { fetchListOfStatusesAvailableRequest } from "@Features/reviews/api/reviews.api";
import IconComponent from "@CubeComponents/icons";
import { changeResourceStatusInReview, getReviewsListForEntity, getStatusBtnStyle } from "./ReviewsHelper";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ChangeFileStatusModal from "./ChangeFileStatusModal";
import { getReviewUrl } from "@Features/projects/utils/projectUrls";
import _ from "lodash";
import Text from "@CubeComponents/html/Text/Text";
import {
  ReviewsListForEntityProps,
  SignatureModalState,
  SignatureModalData,
  DropdownItemTemplateArgs,
  Status,
} from "./reviews.interfaces";
import type {
  GetReviewsListForEntityResponse,
  ReviewStatusItem,
  ChangeResourceStatusInReviewRequest,
} from "@ServerTypes/reviews/reviews.schema";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import { Box } from "@mui/material";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * One row of `getReviewsListForEntityResponse.result` — replaces the hand-written `Review` interface in
 * reviews.interfaces.ts, which duplicated `reviewsListForEntityItemSchema` field-for-field.
 */
type ReviewForEntity = NonNullable<GetReviewsListForEntityResponse["result"]>[number];

/** One entry of that row's `approvalStages` — replaces the hand-written `ApprovalStage` interface. */
type ReviewForEntityApprovalStage = NonNullable<ReviewForEntity["approvalStages"]>[number];

/** One `changeResourceStatusInReviewRequest.resources[]` entry, whose `resourceType` is an enum. */
type ChangeStatusResource = ChangeResourceStatusInReviewRequest["resources"][number];

/**
 * The RESOURCE_TYPE_* values that request accepts. Declared as a complete `Record` of the schema's
 * enum so adding or renaming a member there fails to compile here instead of silently drifting.
 */
const CHANGE_STATUS_RESOURCE_TYPES: Record<NonNullable<ChangeStatusResource["resourceType"]>, true> = {
  RESOURCE_TYPE_FILE: true,
  RESOURCE_TYPE_ESTIMATE: true,
  RESOURCE_TYPE_ISSUE: true,
  RESOURCE_TYPE_TASK: true,
  RESOURCE_TYPE_EMBED: true,
};
const isChangeStatusResourceType = (value: string): value is NonNullable<ChangeStatusResource["resourceType"]> =>
  Object.hasOwn(CHANGE_STATUS_RESOURCE_TYPES, value);

/**
 * `reviews.interfaces.Status` declares `_id`/`name`/`category` required-and-nullable while the server
 * rows (`reviewStatusItemSchema`, and the list row's `status`) declare them optional, so a row is not
 * assignable to it. The spread keeps every other field intact — `ChangeFileStatusModal` re-spreads the
 * object — and only the three declared fields are normalised.
 */
type StatusLike = { _id?: string | null; name?: string | null; category?: string | null };
const toStatus = (status: StatusLike | null | undefined): Status => ({
  ...status,
  _id: status?._id ?? null,
  name: status?.name ?? null,
  category: status?.category ?? null,
});

function ReviewsListForEntity({
  resource,
  spaceId,
  includeDefaultReviews = false,
  skipPopUp = false,
  confirmForNonDefault = false,
  successMessage,
  onRequestStatusConfirm,
  canEditDefaultStatus = true,
}: Readonly<ReviewsListForEntityProps>) {
  const { t } = useTranslation();
  const defaultState: SignatureModalState = { show: false, data: null };
  const [reviewsList, setReviewsList] = useState<ReviewForEntity[]>([]);
  const [signatureModal, setSignatureModal] = useState<SignatureModalState>(defaultState);
  const [statusOptionsMap, setStatusOptionsMap] = useState<Record<string, ReviewStatusItem[]>>({});
  const [loading, setLoading] = useState(true);
  const statusRefs = useRef<React.RefObject<HTMLElement | null>[]>([]);

  const dispatch = useDispatch();

  const closeSignatureModal = () => {
    setSignatureModal(defaultState);
    fetchReviewsListForEntity();
  };

  const openReview = (e: React.MouseEvent<HTMLElement>) => {
    const projectId = e.currentTarget.dataset.projectid ?? "";
    const reviewId = e.currentTarget.dataset.reviewid ?? "";
    const spaceId = e.currentTarget.dataset.spaceid ?? "";

    const url = getReviewUrl(projectId, spaceId, reviewId);
    window.open(url, "_blank");
  };

  const saveStatusWithoutConfirmation = async ({
    statusTo,
    reviewId,
    resources,
  }: {
    statusTo: ReviewStatusItem;
    reviewId: string;
    resources: ChangeResourceStatusInReviewRequest["resources"];
  }) => {
    const data = {
      // KNOWN BUG, preserved verbatim: `reviewStatusItemSchema._id` is optional, but
      // `changeResourceStatusInReviewRequest.statusTo` is required, so a status option that arrived
      // without an `_id` would post `statusTo: undefined` and Zod would reject the whole request.
      // Does the server read it? Yes — `statusTo` is the transition target and is re-validated
      // against fetchListOfStatusesAvailable. Fix: make `_id` required in reviewStatusItemSchema.
      statusTo: statusTo._id!,
      reviewId,
      resources,
    };

    const response = await changeResourceStatusInReview(data);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: successMessage ?? t("Status has been updated successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      // Refresh this list so the row reflects the new status (mirrors the confirmation-modal path).
      fetchReviewsListForEntity();
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const entityStatusChanged = (
    newValue: ReviewStatusItem,
    previousStatus: ReviewForEntity["status"],
    reviewId: string,
    approvalStages: ReviewForEntityApprovalStage[] | undefined,
    isDefaultReview = false,
  ) => {
    const statusTo = newValue;

    const currentStage = _.find(approvalStages, {
      clearanceStatus: "in_progress",
    });
    // KNOWN BUG, preserved verbatim: `requireMandatoryComment` is optional on the stage schema but is
    // dereferenced twice without a guard, so a stage saved before that config existed throws here and
    // the status dropdown stops working. Does the server read it? No — it only decides whether the
    // confirmation modal demands a comment. Fix: optional-chain it (changes the emit).
    const requireMandatoryComment = currentStage
      ? currentStage.requireMandatoryComment!.enabled && currentStage.requireMandatoryComment!.values.includes(4)
      : false;

    // For issues (confirmForNonDefault), the default review saves directly while any other review
    // must go through the confirmation popup (R7). Otherwise fall back to the shared skipPopUp flag.
    const skipConfirmation = confirmForNonDefault ? isDefaultReview : skipPopUp;

    if (skipConfirmation) {
      // `ReviewsListForEntityProps.resource.resourceType` is a plain `string` while the request schema
      // declares a RESOURCE_TYPE_* enum; every caller passes one of those constants, and anything else
      // would be rejected by the schema, so it is checked here rather than asserted.
      const resourceForRequest: ChangeStatusResource = {
        ...resource,
        resourceType: isChangeStatusResourceType(resource.resourceType) ? resource.resourceType : undefined,
      };
      saveStatusWithoutConfirmation({
        statusTo,
        reviewId,
        resources: [resourceForRequest],
      });
      return;
    }

    const confirmData: SignatureModalData = {
      reviewId,
      statusTo: toStatus(statusTo),
      statusFrom: toStatus(previousStatus),
      resources: [resource],
      type: "standardTransition",
      requireMandatoryComment,
    };

    // When the caller owns the confirmation (e.g. IssueStatusPopover renders this list inside a
    // popover), hand off the modal so it can close the popover first — otherwise the popover and the
    // modal stack. Without a handler we render the modal inline (Files/Tasks).
    if (onRequestStatusConfirm) {
      onRequestStatusConfirm(confirmData);
    } else {
      setSignatureModal({ show: true, data: confirmData });
    }
  };

  const fetchStatusOptionsForReviews = async (reviews: ReviewForEntity[]) => {
    const newStatusOptionsMap: Record<string, ReviewStatusItem[]> = {};
    await Promise.all(
      reviews.map(async (review: ReviewForEntity) => {
        try {
          // `reviewId` is optional on the response row but required by the request schema; every row
          // the aggregation emits carries it. Erased assertion, no guard added.
          const statusResponse = await fetchListOfStatusesAvailableRequest({
            value: { params: { reviewId: review.reviewId!, resource } },
          });
          if (statusResponse.data.result) {
            newStatusOptionsMap[review.reviewId!] = statusResponse.data.result;
          }
        } catch (e) {
          console.error("Error fetching status options:", e);
          Promise.reject(e);
        }
      }),
    );
    setStatusOptionsMap(newStatusOptionsMap);
  };

  const fetchReviewsListForEntity = async () => {
    setLoading(true);
    try {
      // `ReviewsListForEntityProps.spaceId` is plain `string` because `IssueStatusPopover` feeds it
      // `IssueStatusPopoverIssue.spaceId` (issues feature), which its own callers build with `?? ""`,
      // while the request requires a 24-hex `objectIdString` — so it is validated here. A malformed id
      // could only ever have been rejected by the server, leaving the list untouched.
      const parsedSpaceId = objectIdString.safeParse(spaceId);
      if (!parsedSpaceId.success) return;

      const params = {
        resource: {
          resourceType: resource.resourceType,
          fileVersionId: resource.fileVersionId,
          taskId: resource.taskId,
          issueId: resource.issueId,
        },
        spaceId: parsedSpaceId.data,
        includeDefaultReviews,
      };
      const response = await getReviewsListForEntity({ value: { params } });

      if (response.data.status) {
        setReviewsList(response.data.result!);
        await fetchStatusOptionsForReviews(response.data.result!);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviewsListForEntity();
  }, [resource.fileVersionId, resource.taskId, resource.issueId, resource.resourceType]);

  const _dropdownItemTemplate = (args: DropdownItemTemplateArgs) => {
    const style = getStatusBtnStyle(args.category);

    return (
      <div className="py-2 px-3">
        <span style={style} className="rounded py-1 px-4 text-sm text-white">
          {args.name}
        </span>
      </div>
    );
  };

  return (
    <>
      {loading && !reviewsList.length ? (
        <div className="py-4 text-sm text-text_light">{t("Loading...")}</div>
      ) : reviewsList.length ? (
        <div className="flex flex-col gap-6 overflow-auto py-2">
          {reviewsList.length
            ? reviewsList.map((review: ReviewForEntity, index: number) => {
                const { name, status, spaceId, projectId, reviewId, isDefaultReview, serialId, approvalStages } =
                  review;

                if (!statusRefs.current[index]) {
                  statusRefs.current[index] = React.createRef<HTMLElement>();
                }

                const statusStyle = status?.category ? getStatusBtnStyle(status.category) : undefined;

                const statusDropdownEl = (
                  <AutoComplete
                    options={statusOptionsMap[reviewId!] || []}
                    disabled={isDefaultReview && !canEditDefaultStatus}
                    valueField="_id"
                    width={"100%"}
                    minWidth={0}
                    getOptionLabel={(option) => option.name || ""}
                    value={(statusOptionsMap[reviewId!] || []).find((opt) => opt._id === status?._id) || null}
                    onChange={(_event, newValue) => {
                      if (newValue) {
                        entityStatusChanged(newValue, status, reviewId!, approvalStages, isDefaultReview);
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div className="py-2 w-full">
                          <Text
                            style={getStatusBtnStyle(option.category)}
                            className="rounded py-1 px-4 text-sm text-white"
                          >
                            {option.name}
                          </Text>
                        </div>
                      </li>
                    )}
                    sx={
                      statusStyle
                        ? {
                            "& .MuiInputBase-root": {
                              background: `${statusStyle.backgroundColor} !important`,
                              borderRadius: "0.25rem",
                              fontWeight: 600,
                            },
                            "& .MuiOutlinedInput-input": {
                              background: "transparent !important",
                              color: "#fff !important",
                              fontSize: "0.875rem",
                              fontWeight: 600,
                            },
                            "& .MuiSvgIcon-root": { color: "#fff" },
                            "& .MuiOutlinedInput-notchedOutline": { border: "none !important" },
                          }
                        : undefined
                    }
                    slotProps={{
                      listbox: { sx: { padding: 0 } },
                      popper: {
                        placement: "bottom-start",
                        style: { width: "12rem" },
                      },
                    }}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                      <TextFieldComponent {...params} name="status" />
                    )}
                  />
                );

                return (
                  <div key={reviewId} className="flex flex-row items-center gap-3">
                    {/* Default review -> info icon (non-navigable); others -> #serialId that swaps to the
                        open-review icon on hover and navigates to that review. */}
                    <div className="group flex w-[2rem] shrink-0 items-center">
                      {isDefaultReview ? (
                        <IconComponent
                          name="details"
                          toolTipContent={t(
                            "This is the default review for the issue. The status can be changed but the review can not be accessed",
                          )}
                          position="top"
                        />
                      ) : (
                        <>
                          <span className="text-sm group-hover:hidden">{`#${serialId}`}</span>
                          <Box
                            data-reviewid={reviewId}
                            data-spaceid={spaceId}
                            data-projectid={projectId}
                            onClick={openReview}
                            className="hidden cursor-pointer items-center group-hover:flex"
                          >
                            <IconComponent name="openInViewer" className="w-5 h-5" />
                          </Box>
                        </>
                      )}
                    </div>
                    {/* Text shows the full review name as a tooltip when it overflows. */}
                    <div className="flex flex-1 min-w-0 gap-3 items-center">
                      {/* A review name is content, not copy, so it goes through `untranslated()`. */}
                      <Text
                        className="min-w-0 flex-2 text-sm font-semibold"
                        tooltipContent={name === undefined ? undefined : untranslated(name)}
                      >
                        {name}
                      </Text>
                      <div className="min-w-0 flex-3">{statusDropdownEl}</div>
                    </div>
                  </div>
                );
              })
            : undefined}
        </div>
      ) : null}
      {signatureModal.show && signatureModal.data && (
        <ChangeFileStatusModal
          {...signatureModal.data}
          closeModal={closeSignatureModal}
          successMessage={successMessage}
        />
      )}
    </>
  );
}

export default ReviewsListForEntity;
